import { ICoordinates } from "../../../types";

const MIN_DISTANCE = 50;
const MAX_ATTEMPTS = 150;

export const calculateDistance = (
  x1: number,
  y1: number,
  x2: number,
  y2: number
): number => {
  return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
};

export const hasCollision = (
  x: number,
  y: number,
  coordinates: ICoordinates[],
  currentId: number | string,
  minDistance: number = MIN_DISTANCE
): boolean => {
  return coordinates.some((coord) => {
    if (coord.id === currentId) return false;
    const distance = calculateDistance(x, y, coord.xStart, coord.yStart);
    return distance < minDistance;
  });
};

export const applySpiralOffset = (
  originalX: number,
  originalY: number,
  coordinates: ICoordinates[],
  currentId: number | string,
  minDistance: number = MIN_DISTANCE,
  maxAttempts: number = MAX_ATTEMPTS
): { x: number; y: number } => {
  if (!hasCollision(originalX, originalY, coordinates, currentId, minDistance)) {
    return { x: originalX, y: originalY };
  }

  const angleStep = 30;
  const radiusStep = 6;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const angle = (attempt * angleStep * Math.PI) / 180;
    const radius = attempt * radiusStep;

    const newX = originalX + radius * Math.cos(angle);
    const newY = originalY + radius * Math.sin(angle);

    if (!hasCollision(newX, newY, coordinates, currentId, minDistance)) {
      return { x: newX, y: newY };
    }
  }

  console.warn(
    `Не удалось найти свободное место для точки ${currentId} после ${maxAttempts} попыток`
  );
  return { x: originalX, y: originalY };
};

export const applyJittering = (
  originalX: number,
  originalY: number,
  coordinates: ICoordinates[],
  currentId: number | string,
  jitterRadius: number = 8
): { x: number; y: number } => {
  if (!hasCollision(originalX, originalY, coordinates, currentId)) {
    return { x: originalX, y: originalY };
  }

  const angle = Math.random() * 2 * Math.PI;
  const radius = Math.random() * jitterRadius;

  const newX = originalX + radius * Math.cos(angle);
  const newY = originalY + radius * Math.sin(angle);

  return { x: newX, y: newY };
};

export const applyForceDirected = (
  originalX: number,
  originalY: number,
  coordinates: ICoordinates[],
  currentId: number | string,
  repulsionForce: number = 20,
  minDistance: number = MIN_DISTANCE
): { x: number; y: number } => {
  let x = originalX;
  let y = originalY;
  const iterations = 12;

  for (let iter = 0; iter < iterations; iter++) {
    let totalForceX = 0;
    let totalForceY = 0;

    coordinates.forEach((coord) => {
      if (coord.id === currentId) return;

      const distance = calculateDistance(x, y, coord.xStart, coord.yStart);

      if (distance < minDistance && distance > 0) {
        const force = Math.pow((minDistance - distance) / distance, 1.5);
        const angle = Math.atan2(y - coord.yStart, x - coord.xStart);

        totalForceX += Math.cos(angle) * force * repulsionForce;
        totalForceY += Math.sin(angle) * force * repulsionForce;
      }
    });

    x += totalForceX;
    y += totalForceY;

    // Early exit if force is negligible
    if (Math.abs(totalForceX) < 0.1 && Math.abs(totalForceY) < 0.1) {
      break;
    }
  }

  return { x, y };
};

export const avoidCollisions = (
  originalX: number,
  originalY: number,
  coordinates: ICoordinates[],
  currentId: number | string,
  strategy: "spiral" | "jitter" | "force" | "combined" = "combined"
): { x: number; y: number } => {
  switch (strategy) {
    case "spiral":
      return applySpiralOffset(originalX, originalY, coordinates, currentId);

    case "jitter":
      return applyJittering(originalX, originalY, coordinates, currentId);

    case "force":
      return applyForceDirected(originalX, originalY, coordinates, currentId);

    case "combined":
    default:
      // Step 1: force-directed
      const forceResult = applyForceDirected(
        originalX,
        originalY,
        coordinates,
        currentId,
        70,
        MIN_DISTANCE
      );

      // Step 2: fallback to spiral if still colliding
      if (
        hasCollision(
          forceResult.x,
          forceResult.y,
          coordinates,
          currentId,
          MIN_DISTANCE * 0.98
        )
      ) {
        return applySpiralOffset(
          forceResult.x,
          forceResult.y,
          coordinates,
          currentId,
          MIN_DISTANCE * 0.98,
          MAX_ATTEMPTS
        );
      }

      return forceResult;
  }
};

export const getPointsInSameArea = (
  coordinates: ICoordinates[],
  segmentId: number | string,
  maturity: string
): ICoordinates[] => {
  return coordinates.filter(
    (coord) =>
      (coord as any).segmentId === segmentId &&
      (coord as any).maturity === maturity
  );
};
